
import React, { useState } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';

interface VideoPlayerProps {
  videoUrl: string;
  title: string;
  className?: string;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl, title, className = "" }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState(false);

  const handlePlayPause = (video: HTMLVideoElement) => {
    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch(() => setError(true));
    }
    setIsPlaying(!isPlaying);
  };

  const handleMuteToggle = (video: HTMLVideoElement) => {
    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  if (error) {
    return (
      <div className={`bg-slate-800 rounded-lg p-6 text-center ${className}`}>
        <p className="text-white mb-2">Unable to load video</p>
        <p className="text-slate-400 text-sm">{title}</p>
      </div>
    );
  }

  return (
    <div className={`relative bg-slate-900 rounded-lg overflow-hidden shadow-lg ${className}`}>
      <video
        className="w-full h-auto"
        src={videoUrl}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onError={() => setError(true)}
        controls
        preload="metadata"
      >
        Your browser does not support the video tag.
      </video>
      
      <div className="absolute bottom-4 left-4 right-4 bg-black/50 backdrop-blur-sm rounded-lg p-3">
        <div className="flex items-center justify-between">
          <h4 className="text-white font-medium text-sm truncate flex-1">{title}</h4>
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={(e) => {
                const video = e.currentTarget.closest('.relative')?.querySelector('video') as HTMLVideoElement;
                if (video) handlePlayPause(video);
              }}
              className="text-white hover:text-amber-400 transition-colors"
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? <Pause size={20} /> : <Play size={20} />}
            </button>
            <button
              onClick={(e) => {
                const video = e.currentTarget.closest('.relative')?.querySelector('video') as HTMLVideoElement;
                if (video) handleMuteToggle(video);
              }}
              className="text-white hover:text-amber-400 transition-colors"
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
